// Build Your Own Discovery Set — cart reconciliation.
//
// Mirrors the cart-mutation behavior of the legacy `BYODiscoverySet` Shopify
// Script on the storefront, before checkout. The pricing piece (zero-pricing
// sample lines) is handled separately by the `discount-function` extension.
//
// Rules enforced here:
//   1. Sample lines (line property `_sample`) are capped at quantity 1.
//   2. If the cart has samples but no parent Discovery Set (line property
//      `_discovery_set`), all samples are removed.
//   3. If the cart contains a soap sample (SKU `LYRBS1Z`) and no parent set,
//      the soap sample is removed.
//   4. Each parent Discovery Set keeps only its own samples. The cap is the
//      parent's `_set_size` (10 on the 10-pack template, otherwise 5).

(function () {
  const SOAP_SAMPLE_SKU = 'LYRBS1Z';
  const SAMPLE_PROP = '_sample';
  const PARENT_PROP = '_discovery_set';
  const BUNDLE_PROP = '_bundle_key';
  const SET_SIZE_PROP = '_set_size';
  const DEFAULT_MAX_SAMPLES = 5;

  let reconciling = false;
  let reconcileAgain = false;

  function getProp(item, key) {
    return item.properties && item.properties[key];
  }

  function hasProp(item, key) {
    const value = getProp(item, key);
    return value != null && value !== '';
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('cart.js fetch failed: ' + res.status);
    return res.json();
  }

  async function applyUpdates(updates) {
    if (!Object.keys(updates).length) return;
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) throw new Error('cart/update.js failed: ' + res.status);
    return res.json();
  }

  function maxSamplesForParent(parent) {
    const setSize = parseInt(getProp(parent, SET_SIZE_PROP), 10);
    if (Number.isFinite(setSize) && setSize > 0) return setSize;

    const numbered = Object.keys(parent.properties || {}).filter((key) => /^_sample_\d+$/.test(key));
    if (numbered.length > 0) return numbered.length;

    return DEFAULT_MAX_SAMPLES;
  }

  function matchesParent(sample, parent) {
    const sampleBundle = getProp(sample, BUNDLE_PROP);
    const sampleId = getProp(sample, SAMPLE_PROP);

    const parentBundle = getProp(parent, BUNDLE_PROP);
    const parentId = getProp(parent, PARENT_PROP);

    return (
      (sampleBundle && parentBundle && sampleBundle === parentBundle) ||
      (sampleId && parentId && sampleId === parentId) ||
      (sampleId && parentBundle && sampleId === parentBundle) ||
      (sampleBundle && parentId && sampleBundle === parentId)
    );
  }

  function planUpdates(cart) {
    const items = cart.items || [];
    const parents = items.filter((item) => hasProp(item, PARENT_PROP) && !hasProp(item, SAMPLE_PROP));
    const samples = items.filter((item) => hasProp(item, SAMPLE_PROP));

    const updates = {};

    // No parent = no samples.
    if (parents.length === 0) {
      for (const item of samples) {
        updates[item.key] = 0;
      }

      for (const item of items) {
        if (item.sku === SOAP_SAMPLE_SKU) {
          updates[item.key] = 0;
        }
      }

      return updates;
    }

    // Each sample must belong to a parent and can only have quantity 1.
    const validSamplesByParent = new Map();

    for (const sample of samples) {
      let parent = null;

      // Prefer exact _bundle_key matching.
      const bundleKey = getProp(sample, BUNDLE_PROP);

      if (bundleKey) {
        parent = parents.find((item) => getProp(item, BUNDLE_PROP) === bundleKey);
      }

      // Fall back to legacy property matching.
      if (!parent) {
        parent = parents.find((item) => matchesParent(sample, item));
      }

      if (!parent) {
        updates[sample.key] = 0;
        continue;
      }

      if (sample.quantity > 1) {
        updates[sample.key] = 1;
      }

      const parentKey = getProp(parent, BUNDLE_PROP) || getProp(parent, PARENT_PROP);

      if (!validSamplesByParent.has(parentKey)) {
        validSamplesByParent.set(parentKey, []);
      }

      validSamplesByParent.get(parentKey).push(sample);
    }

    // Limit each Discovery Set to its own size. Legacy 5-packs have no
    // `_set_size`, so fall back to the numbered `_sample_N` properties, then 5.
    for (const [parentKey, bundleSamples] of validSamplesByParent) {
      const parent = parents.find((item) => (getProp(item, BUNDLE_PROP) || getProp(item, PARENT_PROP)) === parentKey);
      const maxSamples = parent ? maxSamplesForParent(parent) : DEFAULT_MAX_SAMPLES;

      if (bundleSamples.length <= maxSamples) {
        continue;
      }

      for (let i = maxSamples; i < bundleSamples.length; i++) {
        updates[bundleSamples[i].key] = 0;
      }
    }

    // Soap sample must belong to a parent.
    for (const item of items) {
      if (
        item.sku === SOAP_SAMPLE_SKU &&
        hasProp(item, SAMPLE_PROP) &&
        !parents.some((parent) => matchesParent(item, parent))
      ) {
        updates[item.key] = 0;
      }
    }

    return updates;
  }

  async function reconcile() {
    if (reconciling) {
      reconcileAgain = true;
      return;
    }

    reconciling = true;
    reconcileAgain = false;

    try {
      const cart = await fetchCart();
      const updates = planUpdates(cart);

      if (Object.keys(updates).length) {
        await applyUpdates(updates);
      }
    } catch (error) {
      console.error('[BYO Discovery Set] reconciliation failed:', error);
    } finally {
      reconciling = false;

      if (reconcileAgain) {
        reconcileAgain = false;
        setTimeout(reconcile, 0);
      }
    }
  }

  function instrumentCartMutations() {
    const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    const originalFetch = window.fetch.bind(window);

    window.fetch = async function (input, init) {
      const response = await originalFetch(input, init);
      const url = typeof input === 'string' ? input : input?.url || '';

      if (CART_MUTATION.test(url) && response.ok && !reconciling) {
        setTimeout(reconcile, 0);
      }

      return response;
    };

    const OrigXHR = window.XMLHttpRequest;
    if (OrigXHR) {
      const origOpen = OrigXHR.prototype.open;
      const origSend = OrigXHR.prototype.send;

      OrigXHR.prototype.open = function (method, url) {
        this.__byoUrl = url;
        return origOpen.apply(this, arguments);
      };

      OrigXHR.prototype.send = function () {
        this.addEventListener('load', function () {
          if (
            this.__byoUrl &&
            CART_MUTATION.test(this.__byoUrl) &&
            this.status >= 200 &&
            this.status < 300 &&
            !reconciling
          ) {
            setTimeout(reconcile, 0);
          }
        });

        return origSend.apply(this, arguments);
      };
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  instrumentCartMutations();
})();
